import { useState } from "react";
import { Button, Modal } from "@astrum/ui";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import "./first-run-tour.css";

export const TOUR_STEPS: { title: string; body: string; where: string }[] = [
  { title: "Chat", where: "Chat in the sidebar", body: "Channels and direct messages. Bold channels have unread messages; @mention someone to notify them." },
  { title: "Work", where: "Work in the sidebar", body: "Projects and tasks. Open a project for its board, status updates and files; Tasks shows only what is assigned to you." },
  { title: "Schedule", where: "Schedule in the sidebar", body: "Meetings and deadlines for your office, with invitations and reminders." },
  { title: "Notifications", where: "the bell", body: "Mentions, assignments and invitations land here. Mark everything read in one click." },
  { title: "Your profile", where: "your name", body: "Set your name and photo, choose which notifications you get, and turn on two-factor sign-in." },
];

/** Marks the tour finished (or not) server-side, then refreshes the viewer. */
export async function setTourCompleted(completed: boolean, refresh: () => Promise<void>) {
  await api.put("/auth/tour", { completed });
  await refresh();
}

/**
 * Shown once per user: `tourCompletedAt` lives on the account, so it follows
 * the person across browsers and the desktop app. Skip and finish both set it;
 * Help can clear it to run the tour again.
 */
export function FirstRunTour() {
  const { viewer, refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [closing, setClosing] = useState(false);
  // Absent (older server) is treated as already seen, never as a surprise tour.
  if (!viewer || viewer.tourCompletedAt !== null) return null;

  const done = async () => {
    setClosing(true);
    try {
      await setTourCompleted(true, refresh);
    } catch {
      // Hide it for this session even if the save failed; it comes back next load.
      setClosing(false);
      setStep(-1);
    }
  };
  if (step < 0) return null;

  const current = TOUR_STEPS[step]!;
  const last = step === TOUR_STEPS.length - 1;
  return (
    <Modal open onClose={() => void done()} title="Welcome to Astrum Connect">
      <div className="tour" role="group" aria-label={`Tour step ${step + 1} of ${TOUR_STEPS.length}`}>
        <div className="tour__progress">
          <p className="tour__count">
            {String(step + 1).padStart(2, "0")} / {String(TOUR_STEPS.length).padStart(2, "0")}
          </p>
          <div className="tour__bar" aria-hidden="true">
            {TOUR_STEPS.map((s, i) => (
              <span key={s.title} data-done={i <= step ? "" : undefined} />
            ))}
          </div>
        </div>
        <h3 className="tour__title">{current.title}</h3>
        <p className="tour__body">{current.body}</p>
        <p className="tour__where">
          Find it under <strong>{current.where}</strong>.
        </p>
        <div className="tour__actions">
          <Button variant="ghost" className="tour__skip" onClick={() => void done()} disabled={closing}>
            Skip tour
          </Button>
          {step > 0 && (
            <Button variant="secondary" onClick={() => setStep(step - 1)} disabled={closing}>
              Back
            </Button>
          )}
          <Button
            data-autofocus
            onClick={() => (last ? void done() : setStep(step + 1))}
            disabled={closing}
          >
            {last ? "Done" : "Next"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
